import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

/** Pulls its first child gently towards the pointer. Fine pointers only. */
export const Magnetic: FC<MagneticProps> = ({ children, strength = 0.22, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const host = ref.current
        const target = host?.firstElementChild as HTMLElement | null

        if (!host || !target) return
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const move = (event: PointerEvent): void => {
            const rect = host.getBoundingClientRect()
            const x = event.clientX - (rect.left + rect.width / 2)
            const y = event.clientY - (rect.top + rect.height / 2)

            target.style.transform = `translate3d(${x * strength}px, ${y * strength}px, 0)`
        }

        const leave = (): void => {
            target.style.transform = ''
        }

        host.addEventListener('pointermove', move)
        host.addEventListener('pointerleave', leave)

        return () => {
            host.removeEventListener('pointermove', move)
            host.removeEventListener('pointerleave', leave)
            target.style.transform = ''
        }
    }, [strength])

    return (
        <span ref={ref} className={cn('inline-flex', className)}>
            {children}
        </span>
    )
}
